import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileActionBar } from "@/components/MobileActionBar";
import { DOCTOR, SITE_URL } from "@/config/doctorConfig";

const pages = {
  "privacy-policy": {
    title: "Privacy Policy",
    sections: [
      { h: "Information we collect", p: ["When you request an appointment through this website, we collect your name, mobile number, preferred date and time, and the reason for your consultation. This information is used solely to arrange and confirm your appointment."] },
      { h: "How your information is used", p: ["Your details are shared only with the clinic team managing appointments. We do not sell, rent or publish your personal or medical information. Appointment details submitted through the form are stored securely and are not displayed publicly on this website."] },
      { h: "Third-party services", p: ["This website uses Google Maps to display the clinic location and provides links to WhatsApp for communication. These services are governed by their own privacy policies."] },
      { h: "Your choices", p: [`If you would like your appointment information to be corrected or deleted, please contact the clinic at ${DOCTOR.clinicPhoneDisplay}.`] },
    ],
  },
  "terms-of-use": {
    title: "Terms of Use",
    sections: [
      { h: "Purpose of this website", p: [`This website presents the professional profile of ${DOCTOR.name}, ${DOCTOR.qualifications}, and provides general health education and a way to request appointments at ${DOCTOR.hospitalName}, Bhopal.`] },
      { h: "No doctor–patient relationship", p: ["Use of this website, including reading articles or submitting an appointment request, does not by itself create a doctor–patient relationship. Medical advice is provided only during a consultation."] },
      { h: "Accuracy of content", p: ["Health articles are prepared for general education and are reviewed for medical responsibility. However, medical knowledge evolves, and content may not apply to your individual situation. Always consult a qualified physician for personal medical advice."] },
      { h: "Acceptable use", p: ["You agree not to misuse this website, submit false information through the appointment form, or attempt to disrupt its operation."] },
    ],
  },
  "medical-disclaimer": {
    title: "Medical Disclaimer",
    sections: [
      { h: "Educational content only", p: ["The information provided on this website is for general educational purposes only and should not be considered a substitute for an in-person medical consultation, diagnosis or treatment."] },
      { h: "No guaranteed outcomes", p: ["Medical outcomes vary from person to person. Nothing on this website should be interpreted as a guarantee of cure, recovery or a specific treatment result."] },
      { h: "Do not self-medicate", p: ["Do not start, stop or change any medication based on information on this website without consulting a qualified physician."] },
      { h: "Medical emergencies", p: ["In case of a medical emergency, please contact emergency services or visit the nearest emergency department immediately. Do not rely on this website, email or WhatsApp for urgent medical care."] },
    ],
  },
};

export default function LegalPage({ pageKey }) {
  const page = pages[pageKey];
  return (
    <>
      <Helmet>
        <title>{`${page.title} | ${DOCTOR.name}`}</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/${pageKey}`} />
        <meta property="og:url" content={`${SITE_URL}/${pageKey}`} />
      </Helmet>
      <Header />
      <main className="pt-28 lg:pt-36 pb-20 bg-background min-h-screen" data-testid={`legal-page-${pageKey}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-widest text-teal-700 mb-4">Legal</p>
          <h1 className="font-serif font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">{page.title}</h1>
          <div className="mt-10 space-y-8">
            {page.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-serif font-semibold text-xl text-slate-900 mb-2.5">{s.h}</h2>
                {s.p.map((para, i) => (
                  <p key={i} className="text-sm sm:text-base text-slate-600 leading-relaxed">{para}</p>
                ))}
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
